import React, { useState, useEffect } from 'react';
import { useFoodSafetyContext, FS_SCREENS } from '../context/FoodSafetyContext';
import { foodSafetyService } from '../services/foodSafetyService';
import moment from 'moment';

const Dashboard: React.FC = () => {
    const { dispatch } = useFoodSafetyContext();
    const [tours, setTours]= useState<any[]>([]);
    const [filteredTours, setFilteredTours] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedTour, setSelectedTour] = useState<any | null>(null);
    const [breakdownItems, setBreakdownItems] = useState<any[]>([]);
    const [isLoadingBreakdown, setIsLoadingBreakdown] = useState(false);

    // Filter state
    const [filterSite, setFilterSite] = useState('All');
    const [filterLine, setFilterLine] = useState('All');
    const [filterType, setFilterType] = useState('All');
    const [filterStart, setFilterStart] = useState('');
    const [filterEnd, setFilterEnd] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                setIsLoading(true);
                const data = await foodSafetyService.getTourHistory();
                setTours(data || []);
            } catch (err) {
                console.error("Dashboard: Failed to load tour history", err);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, []);

    useEffect(() => {
        const start = filterStart ? moment(filterStart).startOf('day') : null;
        const end = filterEnd ? moment(filterEnd).endOf('day') : null;

        const filtered = (tours || []).filter(t => {
            if (filterSite !== 'All' && t.cr3ea_plantid && t.cr3ea_plantid !== filterSite) return false;
            if (filterLine !== 'All' && t.cr3ea_lineno && t.cr3ea_lineno !== filterLine) return false;
            if (filterType !== 'All' && t.cr3ea_food_safety_checklisttype && t.cr3ea_food_safety_checklisttype !== filterType) return false;
            if (t.cr3ea_tourstartdate) {
                const tourDate = moment(t.cr3ea_tourstartdate);
                if (start && tourDate.isBefore(start)) return false;
                if (end && tourDate.isAfter(end)) return false;
            }
            return true;
        });
        setFilteredTours(filtered);
        setSelectedTour(null);
        setBreakdownItems([]);
    }, [tours, filterSite, filterLine, filterType, filterStart, filterEnd]);

    // Metrics
    const total = filteredTours.length;
    const passCount = filteredTours.filter(t => t.cr3ea_checklist_result === 'Pass').length;
    const scores = filteredTours.map(t => parseFloat(t.cr3ea_overall_score)).filter(n => !isNaN(n));
    const avgScore = scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) + '%' : 'N/A';
    const successRate = total > 0 ? ((passCount / total) * 100).toFixed(1) + '%' : '0.0%';

    const loadBreakdown = async (tour: any) => {
        setSelectedTour(tour);
        setIsLoadingBreakdown(true);
        try {
            const items = await foodSafetyService.getChecklistItems(tour.cr3ea_prod_rajpura_quality_tourid);
            setBreakdownItems(items);
        } catch (err) {
            console.error("Dashboard: Failed to load breakdown", err);
        } finally {
            setIsLoadingBreakdown(false);
        }
    };

    // SVG Line Chart
    const renderTrendChart = () => {
        const dataPoints = filteredTours
            .filter(t => t.cr3ea_overall_score && t.cr3ea_overall_score !== 'N/A' && t.cr3ea_tourstartdate)
            .map(t => ({
                date: moment(t.cr3ea_tourstartdate),
                score: parseFloat(t.cr3ea_overall_score)
            }))
            .sort((a, b) => a.date.valueOf() - b.date.valueOf());

        if (dataPoints.length === 0) {
            return (
                <div className="d-flex align-items-center justify-content-center bg-light rounded" style={{ height: 200 }}>
                    <span className="text-secondary">No score trend data for current selection.</span>
                </div>
            );
        }

        const width = 800, height = 220, padding = 40;
        const chartW = width - padding * 2;
        const chartH = height - padding * 2;

        const pts = dataPoints.map((dp, idx) => ({
            x: padding + (dataPoints.length > 1 ? (idx / (dataPoints.length - 1)) * chartW : chartW / 2),
            y: padding + chartH - (dp.score / 100) * chartH,
            score: dp.score,
            dateStr: dp.date.format("DD MMM")
        }));

        const pathD = pts.map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt.x} ${pt.y}`).join(' ');

        const gridLines = [0, 25, 50, 75, 100].map(step => {
            const y = padding + chartH - (step / 100) * chartH;
            return `<line x1="${padding}" y1="${y}" x2="${width - padding}" y2="${y}" stroke="#e2e8f0" stroke-width="1" stroke-dasharray="4,4"/>
                    <text x="${padding - 8}" y="${y + 4}" fill="#64748b" font-size="10" text-anchor="end">${step}%</text>`;
        }).join('');

        const dots = pts.map(pt =>
            `<circle cx="${pt.x}" cy="${pt.y}" r="4" fill="#0284c7" stroke="#fff" stroke-width="2"/>
             <text x="${pt.x}" y="${pt.y - 8}" fill="#0f172a" font-size="10" font-weight="bold" text-anchor="middle">${pt.score.toFixed(0)}%</text>
             <text x="${pt.x}" y="${height - 10}" fill="#64748b" font-size="9" text-anchor="middle">${pt.dateStr}</text>`
        ).join('');

        return (
            <div dangerouslySetInnerHTML={{ __html: `<svg viewBox="0 0 ${width} ${height}" style="width:100%;height:auto;display:block;overflow:visible">
                ${gridLines}
                <path d="${pathD}" fill="none" stroke="#0284c7" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                ${dots}
            </svg>` }} />
        );
    };

    // Area-wise aggregation for breakdown panel
    const areaMap: Record<string, { total: number; notOkay: number }> = {};
    breakdownItems.forEach(item => {
        const area = item.cr953_food_safety_area || 'General';
        if (!areaMap[area]) areaMap[area] = { total: 0, notOkay: 0 };
        areaMap[area].total++;
        if (selectedTour?.cr3ea_food_safety_checklisttype === 'PPE Checklist') {
            if (item.cr953_food_safety_defectcategory === 'Non-Compliant' || parseInt(item.cr953_food_safety_defectcount) > 0) areaMap[area].notOkay++;
        } else {
            if (item.cr953_food_safety_status === 'Not Okay') areaMap[area].notOkay++;
        }
    });

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-bold mb-0"><i className="bi bi-graph-up me-2 text-primary"></i>Food Safety Analytics Dashboard</h4>
                <button className="btn btn-primary" onClick={() => dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.CHECKLIST_INFO })}>
                    <i className="bi bi-plus-circle me-2"></i>New Audit Tour
                </button>
            </div>

            {/* Filters */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-3">
                    <div className="row g-2 align-items-end">
                        <div className="col-md-2 col-sm-6">
                            <label className="form-label small mb-1 fw-semibold">Site</label>
                            <select className="form-select form-select-sm" value={filterSite} onChange={e => setFilterSite(e.target.value)}>
                                <option value="All">All Sites</option>
                                <option value="Rajpura">Rajpura</option>
                                <option value="Phillaur">Phillaur</option>
                                <option value="Indore">Indore</option>
                                <option value="Tahliwal">Tahliwal</option>
                            </select>
                        </div>
                        <div className="col-md-2 col-sm-6">
                            <label className="form-label small mb-1 fw-semibold">Checklist Type</label>
                            <select className="form-select form-select-sm" value={filterType} onChange={e => setFilterType(e.target.value)}>
                                <option value="All">All Types</option>
                                <option value="PPE Checklist">PPE</option>
                                <option value="GMP Checklist">GMP</option>
                                <option value="PCI Checklist">PCI</option>
                            </select>
                        </div>
                        <div className="col-md-2 col-sm-6">
                            <label className="form-label small mb-1 fw-semibold">Line</label>
                            <select className="form-select form-select-sm" value={filterLine} onChange={e => setFilterLine(e.target.value)}>
                                <option value="All">All Lines</option>
                                {["Line 1", "Line 2", "Line 3", "Line 4", "Line 5", "Line 6", "Line 7", "Line 8", "FFS"].map(l => (
                                    <option key={l} value={l}>{l}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2 col-sm-6">
                            <label className="form-label small mb-1 fw-semibold">Start Date</label>
                            <input type="date" className="form-control form-control-sm" value={filterStart} onChange={e => setFilterStart(e.target.value)} />
                        </div>
                        <div className="col-md-2 col-sm-6">
                            <label className="form-label small mb-1 fw-semibold">End Date</label>
                            <input type="date" className="form-control form-control-sm" value={filterEnd} onChange={e => setFilterEnd(e.target.value)} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Metrics */}
            <div className="row g-3 mb-4">
                {[
                    { label: "Total Audits", value: total, icon: "bi-clipboard-check", color: "primary" },
                    { label: "Avg. Score", value: avgScore, icon: "bi-percent", color: "info" },
                    { label: "Success Rate", value: successRate, icon: "bi-graph-up-arrow", color: "success" },
                ].map(m => (
                    <div className="col-md-4" key={m.label}>
                        <div className={`card border-0 shadow-sm text-${m.color}`}>
                            <div className="card-body d-flex align-items-center gap-3 py-3">
                                <i className={`bi ${m.icon} fs-1`}></i>
                                <div>
                                    <p className="mb-0 text-secondary small">{m.label}</p>
                                    <p className="mb-0 fs-4 fw-bold">{m.value}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Trend Chart */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white py-3 border-bottom-0">
                    <h6 className="mb-0 fw-bold">Score Trend</h6>
                </div>
                <div className="card-body p-3">{renderTrendChart()}</div>
            </div>

            {/* Audit History Table */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white py-3 border-bottom-0 d-flex justify-content-between">
                    <h6 className="mb-0 fw-bold">Audit History</h6>
                    <span className="badge bg-secondary">{filteredTours.length} records</span>
                </div>
                <div className="card-body p-0">
                    {isLoading ? (
                        <div className="d-flex justify-content-center py-4">
                            <div className="spinner-border text-primary" role="status"></div>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="px-4">Date</th>
                                        <th>Checklist Type</th>
                                        <th>Site</th>
                                        <th>Line</th>
                                        <th>Score</th>
                                        <th>Result</th>
                                        <th>QA Executive</th>
                                        <th className="text-end pe-4">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTours.length === 0 ? (
                                        <tr><td colSpan={8} className="text-center py-4 text-secondary">No matching records found.</td></tr>
                                    ) : filteredTours.map(t => (
                                        <tr
                                            key={t.cr3ea_prod_rajpura_quality_tourid}
                                            style={{ cursor: 'pointer' }}
                                            className={selectedTour?.cr3ea_prod_rajpura_quality_tourid === t.cr3ea_prod_rajpura_quality_tourid ? 'table-active' : ''}
                                            onClick={() => loadBreakdown(t)}
                                            title="Click to view breakdown"
                                        >
                                            <td className="px-4">{t.cr3ea_tourstartdate ? moment(t.cr3ea_tourstartdate).format("DD-MM-YYYY hh:mm A") : "N/A"}</td>
                                            <td className="fw-semibold">{t.cr3ea_food_safety_checklisttype}</td>
                                            <td>{t.cr3ea_plantid || 'N/A'}</td>
                                            <td>{t.cr3ea_lineno || 'N/A'}</td>
                                            <td className="fw-bold text-primary">{t.cr3ea_overall_score || 'N/A'}</td>
                                            <td>
                                                <span className={`badge rounded-pill ${t.cr3ea_checklist_result === 'Pass' ? 'bg-success' : 'bg-danger'}`}>
                                                    {t.cr3ea_checklist_result || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="text-secondary">{t.cr3ea_assigned_qa || 'N/A'}</td>
                                            <td className="text-end pe-4" onClick={e => e.stopPropagation()}>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => {
                                                        dispatch({ type: 'SET_TOUR_ID', payload: t.cr3ea_prod_rajpura_quality_tourid });
                                                        dispatch({ type: 'SET_SELECTIONS', payload: { type: t.cr3ea_food_safety_checklisttype, shift: t.cr3ea_shift || 'Shift 1' } });
                                                        dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.SUMMARY });
                                                    }}
                                                >
                                                    <i className="bi bi-file-earmark-text me-1"></i> Summary
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Breakdown Panel */}
            {selectedTour && (
                <div className="card shadow-sm border-0 mb-4">
                    <div className="card-header bg-white py-3 border-bottom-0 d-flex justify-content-between">
                        <h6 className="mb-0 fw-bold">
                            Audit Breakdown: {selectedTour.cr3ea_food_safety_checklisttype}
                            {" "}({moment(selectedTour.cr3ea_tourstartdate).format("DD-MM-YYYY")})
                        </h6>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSelectedTour(null); setBreakdownItems([]); }}>
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                    <div className="card-body p-0">
                        {isLoadingBreakdown ? (
                            <div className="d-flex justify-content-center py-4"><div className="spinner-border text-primary"></div></div>
                        ) : (
                            <div className="row g-0">
                                <div className="col-md-8">
                                    <div className="table-responsive">
                                        <table className="table table-sm table-hover align-middle mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th className="px-3">#</th>
                                                    <th>Area</th>
                                                    <th>Criteria / Location</th>
                                                    <th>Status</th>
                                                    <th>Remarks</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {breakdownItems.length === 0 ? (
                                                    <tr><td colSpan={5} className="text-center py-3 text-secondary">No checkpoint data found.</td></tr>
                                                ) : breakdownItems.map((item, idx) => {
                                                    let statusText = item.cr953_food_safety_status || 'Okay';
                                                    let remarks = item.cr953_food_safety_defectremarks || '--';

                                                    if (selectedTour.cr3ea_food_safety_checklisttype === 'PPE Checklist') {
                                                        statusText = item.cr953_food_safety_defectcategory || 'Compliant';
                                                        remarks = `Defect Count: ${item.cr953_food_safety_defectcount || 0}`;
                                                    } else if (selectedTour.cr3ea_food_safety_checklisttype === 'PCI Checklist') {
                                                        remarks = item.cr953_food_safety_observationtype
                                                            ? `${item.cr953_food_safety_observationtype} (Count: ${item.cr953_food_safety_defectcount || 0})`
                                                            : '--';
                                                    }

                                                    const isOkay = statusText === 'Okay' || statusText === 'Compliant';
                                                    return (
                                                        <tr key={idx}>
                                                            <td className="px-3">{idx + 1}</td>
                                                            <td className="text-muted small">{item.cr953_food_safety_area || 'General'}</td>
                                                            <td>{item.cr953_food_safety_location || item.cr953_food_safety_criteria}</td>
                                                            <td>
                                                                <span className={`badge rounded-pill ${isOkay ? 'bg-success' : 'bg-danger'}`}>{statusText}</span>
                                                            </td>
                                                            <td className="text-secondary small">{remarks}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="col-md-4 border-start">
                                    <div className="p-3">
                                        <h6 className="fw-bold mb-3">Area-wise Summary</h6>
                                        <table className="table table-sm align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Area</th>
                                                    <th className="text-center">Total</th>
                                                    <th className="text-center">Issues</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.keys(areaMap).length === 0 ? (
                                                    <tr><td colSpan={3} className="text-secondary text-center">No data.</td></tr>
                                                ) : Object.entries(areaMap).map(([area, stats]) => (
                                                    <tr key={area}>
                                                        <td className="fw-semibold small">{area}</td>
                                                        <td className="text-center">{stats.total}</td>
                                                        <td className="text-center">
                                                            <span className={`badge ${stats.notOkay > 0 ? 'bg-danger' : 'bg-success'}`}>{stats.notOkay}</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
